import { css } from "@linaria/core"

import { focusOutline } from "@/styles/mixins.ts"

export const ButtonStyles = {
  Base: css`
    display: flex;
    align-items: center;
  `,

  Kind: {
    Regular: css`
      height: 36px;
      padding: 0 16px;
      border-radius: var(--border-radius-18);

      cursor: pointer;
      transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
      user-select: none;
      white-space: nowrap;

      font-size: var(--small-font-size);
      font-weight: 500;

      color: var(--mask-color);
      background-color: var(--text-color);

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        background-color: var(--secondary-text-color);
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--mask-color);
        background-color: var(--text-color-80);
      }
    `,

    Alternative: css`
      height: 36px;
      padding: 0 16px;
      border-radius: var(--border-radius-18);

      cursor: pointer;
      transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
      user-select: none;
      white-space: nowrap;

      font-size: var(--small-font-size);
      font-weight: 500;

      color: var(--text-color);
      background-color: var(--mask-color);
      border: 1px solid var(--text-color-10-opaque);

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--text-color);
        background-color: var(--text-color-5);
      }
    `,

    Primary: css`
      height: 36px;
      padding: 0 16px;
      border-radius: var(--border-radius-18);

      cursor: pointer;
      transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
      user-select: none;
      white-space: nowrap;

      font-size: var(--small-font-size);
      font-weight: 500;

      color: var(--mask-color);
      background-color: var(--main-color);

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--mask-color);
        background-color: var(--main-color-80);
      }
    `,

    Secondary: css`
      height: 36px;
      padding: 0 16px;
      border-radius: var(--border-radius-18);

      cursor: pointer;
      transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
      user-select: none;
      white-space: nowrap;

      font-size: var(--small-font-size);
      font-weight: 500;

      color: var(--main-color);
      background-color: var(--main-color-10);

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--main-color);
        background-color: var(--main-color-20);
      }
    `,

    Danger: css`
      height: 36px;
      padding: 0 16px;
      border-radius: var(--border-radius-18);

      cursor: pointer;
      transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
      user-select: none;
      white-space: nowrap;

      font-size: var(--small-font-size);
      font-weight: 500;

      color: var(--error-color);
      background-color: var(--error-color-10);

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--error-color);
        background-color: var(--error-color-20);
      }
    `,

    Success: css`
      height: 36px;
      padding: 0 16px;
      border-radius: var(--border-radius-18);

      cursor: pointer;
      transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
      user-select: none;
      white-space: nowrap;

      font-size: var(--small-font-size);
      font-weight: 500;

      color: var(--success-color);
      background-color: var(--success-color-10);

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--success-color);
        background-color: var(--success-color-20);
      }
    `,

    Whoosh: css`
      position: relative;
      overflow: hidden;

      height: 36px;
      padding: 0 16px;
      border-radius: var(--border-radius-18);

      cursor: pointer;
      user-select: none;
      white-space: nowrap;

      font-size: var(--smaller-font-size);
      font-weight: 500;

      color: var(--main-color);
      background: var(--background-color);
      transition: box-shadow 0.3s ease-out;
      box-shadow: inset 0 8px 8px var(--background-color-25), 0 0 15px oklch(73.4% 0.15 291.466deg / 20%);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 200%;
        height: 100%;
        background: linear-gradient(90deg, transparent, var(--main-color-40), transparent);
        transform: translateX(0);
        transition: none;
        opacity: 0;
      }

      @keyframes shimmer {
        0% {
          transform: translateX(0);
          opacity: 0;
        }

        50% {
          opacity: 0.4;
        }

        100% {
          transform: translateX(100%);
          opacity: 0;
        }
      }

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--main-color);
        background: var(--background-color);
        box-shadow: inset 0 8px 8px var(--background-color-25), 0 0 25px oklch(73.4% 0.15 291.466deg / 40%);

        &::before {
          animation: shimmer 0.8s ease-out;
        }
      }
    `,

    Icon: css`
      flex-shrink: 0;
      justify-content: center;

      width: 24px;
      height: 24px;
      padding: 0;

      color: var(--secondary-text-color);

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }

      &:hover:not(:disabled), &[data-force-active="true"] {
        color: var(--text-color);
      }

      .svg-icon {
        width: 24px;
        height: 24px;
      }
    `,

    IconRounded: css`
      flex-shrink: 0;
      justify-content: center;

      width: 36px;
      height: 36px;
      padding: 0;

      color: var(--secondary-text-color);
      background: var(--background-color);
      border-radius: var(--border-radius-round);
      transition: color 0.2s ease-out, box-shadow 0.2s ease-out, transform 0.1s ease-out;

      box-shadow: 0 3px 18px 0 var(--text-color-5), 0 1px 2px 0 var(--background-color-30) inset;

      &:focus-visible {
        ${focusOutline};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
        color: var(--text-color-30);
      }

      &:hover:not(:disabled) {
        color: var(--text-color);
        box-shadow: 0 4px 20px 0 var(--text-color-10), 0 1px 2px 0 var(--background-color-30) inset;
      }

      &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: 0 2px 12px 0 var(--text-color-8), 0 1px 2px 0 var(--background-color-30) inset;
      }

      &[data-force-active="true"] {
        color: var(--text-color);
        box-shadow: 0 3px 18px 0 var(--text-color-10), 0 1px 2px 0 var(--background-color-30) inset;
      }

      .svg-icon {
        width: 20px;
        height: 20px;
      }
    `
  },

  Size: {
    Small: css`
      height: 24px;
      padding: 0 8px;
      border-radius: var(--border-radius-4);

      font-size: var(--smaller-font-size);
    `,

    SmallIcon: css`
      width: 16px;
      height: 16px;

      .svg-icon {
        width: 16px;
        height: 16px;
      }
    `,

    SmallIconRounded: css`
      width: 24px;
      height: 24px;

      .svg-icon {
        width: 16px;
        height: 16px;
      }
    `,

    Large: css`
      height: 40px;
      border-radius: var(--border-radius-20);
      font-size: var(--base-font-size);
    `,

    LargeIconRounded: css`
      width: 40px;
      height: 40px;

      box-shadow: 0 4px 24px 0 var(--text-color-8), 0 2px 4px 0 var(--background-color-30) inset;

      &:hover:not(:disabled) {
        box-shadow: 0 6px 28px 0 var(--text-color-15), 0 2px 4px 0 var(--background-color-30) inset;
      }

      &:active:not(:disabled) {
        box-shadow: inset 0 1px 2px var(--background-color-25), 0 0 15px oklch(73.4% 0.15 291.466deg / 30%);
      }

      .svg-icon {
        width: 20px;
        height: 20px;
      }
    `
  }
}
